import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import KTTable from 'helpers/ui-components/KTTable'
import KTPagination from 'helpers/ui-components/KTPagination'
import Spinner from 'layout/common/Spinner'

import {useListController} from 'hooks/useListController'
import {useEffect, useMemo, useState, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {
 CommonDropdownOption,
 TicketItemDTO,
} from 'models/Ticket'
import {IFormField, ColumnProps} from 'models/Common'
import {
 fetchAllTicketsApi,
 getCommonDropdownApi,
} from 'utils/api/auth/ticketService'
import {useNavigate} from 'react-router-dom'
import {
 formatDate,
 formatDatePayload,
 formatDateTime,
} from 'utils/helper'
import {KTDateRangeFilter} from 'helpers/ui-components/KTDateRangeFilter'
import {fetchState} from 'utils/api/auth/locationService'
import IDropdown from 'models/dropdown'
import {USER_CATEGORY} from 'constants/userCategory'

type IProps = {
 hidePartent: boolean
 setHideParent: (newState: boolean) => void
}

const ROLE_GROUPS = {
 PMU: USER_CATEGORY.PMU_BAYER,
 DISTRIBUTOR: USER_CATEGORY.DISTRIBUTOR_DIRECTOR,
 TSU: USER_CATEGORY.TSU_USER,
} as const

type RoleKey = keyof typeof ROLE_GROUPS

const statusColorMap: Record<string, string> = {
 OPEN: 'text-[#8C6500]',
 RE_OPEN: 'text-[#8C6500]',
 RESOLVED: 'text-[#43A314]',
 CLOSED: 'text-[#337AB8]',
}

const feedbackColorMap: Record<string, string> = {
 SATISFIED: 'text-[#43A314]',
 NOT_SATISFIED: 'text-[#CC293F]',
}

const AllTicketScreen = (props: IProps) => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const userAccess = localStorage.getItem('user')
 const [loader, setLoader] = useState(false)
 const [activeFilter, setActiveFilter] = useState<
  'ALL' | 'OPEN' | 'RESOLVED' | 'CLOSED'
 >('ALL')
 const [priorityOptions, setPriorityOptions] = useState<
  CommonDropdownOption[]
 >([])

 const [selectedPriority, setSelectedPriority] = useState<
  string | undefined
 >(undefined)
 const [isPriorityOpen, setIsPriorityOpen] = useState(false)
 const [stateOptions, setStateOptions] = useState<
  IDropdown[]
 >([])
 const [selectedState, setSelectedState] = useState<
  number | undefined
 >(undefined)
 const [isStateOpen, setIsStateOpen] = useState(false)
 const [filterFormData, setFilterFormData] =
  useState<IFormField>({
   rangeType: 30,
  })
 const priorityDropdownRef = useRef<HTMLDivElement>(null)
 const stateDropdownRef = useRef<HTMLDivElement>(null)
 const userCategoryKey = userAccess
  ? JSON.parse(userAccess)?.userCategoryKey
  : null
 const fetchAllTicketsFetcher = async (
  page: number,
  pageSize: number,
  query?: IFormField
 ): Promise<{data: TicketItemDTO[]; total: number}> => {
  const params = {
   page,
   page_size: pageSize,

   ...(query?.from_date && {
    start_date: formatDatePayload(query.from_date),
   }),

   ...(query?.to_date && {
    end_date: formatDatePayload(query.to_date),
   }),

   ...(query?.search && {search_by: query.search}),
   ...(query?.ticket_status && {
    ticket_status: query.ticket_status,
   }),
   ...(query?.priority !== undefined && {
    priority: query.priority,
   }),
   ...(query?.state !== undefined && {
    state_id: query.state,
   }),
  }
  const res = await fetchAllTicketsApi(params)

  return {
   data: res.results,
   total: res.count,
  }
 }

 const {
  data: tableData,
  total: totalRecord,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  setSearchTerm,
  applyFilters,
 } = useListController<TicketItemDTO>({
  fetcher: fetchAllTicketsFetcher,
  initialPageSize: 10,
  setLoader,
 })
 const quickFilters = useMemo(() => {
  return ['ALL', 'OPEN', 'RESOLVED', 'CLOSED'] as const
 }, [userCategoryKey])

 useEffect(() => {
  setCurrentPage(1)
  applyFilters({
   ...filterFormData,
   priority: selectedPriority,
   state: selectedState,
   ticket_status:
    activeFilter === 'ALL'
     ? undefined
     : activeFilter === 'OPEN'
     ? 'OPEN,RE_OPEN'
     : activeFilter,
  })
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [
  filterFormData,
  activeFilter,
  selectedPriority,
  selectedState,
 ])

 useEffect(() => {
  props.setHideParent(false)
 }, [])

 useEffect(() => {
  const fetchPriority = async () => {
   const res = await getCommonDropdownApi('ticket_priority')
   setPriorityOptions(res || [])
  }

  void fetchPriority()
 }, [])

 useEffect(() => {
  const getStateList = async () => {
   const res: IDropdown[] = await fetchState()
   if (res) {
    setStateOptions(res)
   }
  }

  void getStateList()
 }, [])

 useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
   if (
    priorityDropdownRef.current &&
    !priorityDropdownRef.current.contains(
     event.target as Node
    )
   ) {
    setIsPriorityOpen(false)
   }
   if (
    stateDropdownRef.current &&
    !stateDropdownRef.current.contains(event.target as Node)
   ) {
    setIsStateOpen(false)
   }
  }

  document.addEventListener('mousedown', handleClickOutside)
  return () => {
   document.removeEventListener(
    'mousedown',
    handleClickOutside
   )
  }
 }, [])

 const allColumns: Array<
  ColumnProps<TicketItemDTO> & {showFor?: RoleKey[]}
 > = [
  {
   key: 'ticket_number',
   title: t('ticket.ticketTable.ticketNo'),
   render: (_, record) => (
    <div>
     <p className='text-sm font-semibold text-[#33393D]'>
      {record.ticket_number}
     </p>
     <p className='text-xs text-[#5A6066]'>
      {formatDate(record.ticket_date)}
     </p>
    </div>
   ),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
  {
   key: 'customer_name',
   title: t('ticket.ticketTable.customerName'),
   render: (_, record) => (
    <div>
     <p className=' text-[#33393D]'>
      {record.customer_name}
     </p>
    </div>
   ),
   showFor: ['PMU', 'TSU'],
  },
  {
   key: 'module',
   title: t('ticket.ticketTable.module'),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
  {
   key: 'issue_type',
   title: t('ticket.ticketTable.issueType'),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
  {
   key: 'last_updated',
   title: t('ticket.ticketTable.lastUpdated'),
   render: (_, record) => (
    <span className='text-[#33393D] whitespace-pre-line'>
     {formatDateTime(record.updated_at)}
    </span>
   ),
   showFor: ['DISTRIBUTOR'],
  },
  {
   key: 'ticket_status',
   title: t('ticket.ticketTable.ticketStatus'),
   render: (_, record) => (
    <span
     className={` ${statusColorMap[record.ticket_status]}`}
    >
     {record.ticket_status === 'RE_OPEN'
      ? 'REOPENED'
      : record.ticket_status}
    </span>
   ),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
  {
   key: 'priority',
   title: t('ticket.ticketTable.issuePriority'),
   render: (_, record) => (
    <span className='text-[#33393D]'>
     {record.priority || '--'}
    </span>
   ),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
  {
   key: 'user_feedback_status',
   title: t('ticket.ticketTable.userFeedback'),
   render: (_, record) =>
    record.user_feedback_status ? (
     <span
      className={` ${
       feedbackColorMap[record.user_feedback_status]
      }`}
     >
      {record.user_feedback_status === 'SATISFIED'
       ? record.user_feedback_status
       : 'NOT SATISFIED'}
     </span>
    ) : (
     '-'
    ),
   showFor: ['PMU', 'TSU'],
  },
  {
   key: 'action',
   title: t('ticket.ticketTable.action'),
   className: 'text-center',
   render: (_, record) => (
    <div
     className='inline-flex px-4 py-1.5 border border-[#4E83E4]
                        text-[#4E83E4] rounded-lg cursor-pointer bg-[#F5FAFF]'
     onClick={() => {
      navigate(`/all-tickets/${record.ticket_number}`)
     }}
    >
     {t('ticket.ticketTable.viewDetails')}
    </div>
   ),
   showFor: ['DISTRIBUTOR', 'PMU', 'TSU'],
  },
 ]

 const columns = useMemo(() => {
  if (!userCategoryKey) return []

  return allColumns.filter((col) => {
   if (!col.showFor) return true

   return col.showFor.some(
    (roleKey) => ROLE_GROUPS[roleKey] === userCategoryKey
   )
  })
 }, [userCategoryKey])

 return (
  <>
   {loader && (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/30'>
     <Spinner />
    </div>
   )}
   <KTSearchBox
    label={t('ticket.ticketTopBar.search')}
    placeholder={t('ticket.ticketTopBar.byTicketNumber')}
    setSearchTerm={setSearchTerm}
   />

   <div className='mt-4 flex items-center gap-6 bg-[#F5F8FA] p-3 rounded-lg flex flex-wrap items-start gap-4'>
    <KTDateRangeFilter
     title={t('ticket.ticketTopBar.dateRange')}
     filterFormData={filterFormData}
     setFilterFormData={setFilterFormData}
    />

    <div
     className='flex items-center gap-2 relative'
     ref={priorityDropdownRef}
    >
     <span className='text-sm text-[#33393D]'>
      {t('ticket.ticketTopBar.priority')}
     </span>
     <div
      onClick={() => setIsPriorityOpen(!isPriorityOpen)}
      className='px-2 py-3 rounded-lg border border-[#CED8E0] 
               bg-white text-sm min-w-[120px] cursor-pointer
               flex items-center justify-between'
     >
      <span>
       {selectedPriority
        ? priorityOptions.find(
           (p) => p.value === selectedPriority
          )?.label
        : t('ticket.ticketTopBar.all')}
      </span>
      <svg
       className={`w-4 h-4 transition-transform duration-200 ${
        isPriorityOpen ? 'rotate-180' : ''
       }`}
       viewBox='0 0 20 20'
       fill='none'
       xmlns='http://www.w3.org/2000/svg'
      >
       <path
        d='M5 7L10 12L15 7'
        stroke='#33393D'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
       />
      </svg>
     </div>

     {isPriorityOpen && (
      <div
       className='absolute top-[50px] left-[50px] z-50 bg-[#F5F8FA]
                    w-[195px] rounded-2xl shadow-xl 
                    overflow-hidden border border-[#E5EAF0]'
      >
       <div
        onClick={() => {
         setSelectedPriority(undefined)
         setIsPriorityOpen(false)
        }}
        className='flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#F5F8FA]'
       >
        <div
         className={`w-5 h-5 rounded-full border-2 
          ${
           !selectedPriority
            ? 'border-[#4E83E4]'
            : 'border-[#CED8E0]'
          }
          flex items-center justify-center`}
        >
         {!selectedPriority && (
          <div className='w-3 h-3 bg-[#4E83E4] rounded-full' />
         )}
        </div>
        <span className='text-base text-[#33393D]'>
         {t('ticket.ticketTopBar.all')}
        </span>
       </div>

       <div className='border-t border-[#E5EAF0]' />

       {priorityOptions.map((item) => (
        <div key={item.value}>
         <div
          onClick={() => {
           setSelectedPriority(item.value)
           setIsPriorityOpen(false)
          }}
          className='flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#F5F8FA]'
         >
          <div
           className={`w-5 h-5 rounded-full border-2 
              ${
               selectedPriority === item.value
                ? 'border-[#4E83E4]'
                : 'border-[#CED8E0]'
              }
              flex items-center justify-center`}
          >
           {selectedPriority === item.value && (
            <div className='w-3 h-3 bg-[#4E83E4] rounded-full' />
           )}
          </div>

          <span className='text-base text-[#33393D]'>
           {item.label}
          </span>
         </div>

         <div className='border-t border-[#E5EAF0]' />
        </div>
       ))}
      </div>
     )}
    </div>

    <div
     className='flex items-center gap-2 relative'
     ref={stateDropdownRef}
    >
     <span className='text-sm text-[#33393D]'>
      {t('ticket.ticketTopBar.state', 'State')}
     </span>
     <div
      onClick={() => setIsStateOpen(!isStateOpen)}
      className='px-2 py-3 rounded-lg border border-[#CED8E0] 
               bg-white text-sm min-w-[160px] cursor-pointer
               flex items-center justify-between'
     >
      <span>
       {selectedState
        ? stateOptions.find((s) => s.id === selectedState)
           ?.name
        : t('ticket.ticketTopBar.all')}
      </span>
      <svg
       className={`w-4 h-4 transition-transform duration-200 ${
        isStateOpen ? 'rotate-180' : ''
       }`}
       viewBox='0 0 20 20'
       fill='none'
       xmlns='http://www.w3.org/2000/svg'
      >
       <path
        d='M5 7L10 12L15 7'
        stroke='#33393D'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
       />
      </svg>
     </div>

     {isStateOpen && (
      <div
       className='absolute top-[50px] left-[50px] z-50 bg-[#F5F8FA]
                    w-[280px] rounded-2xl shadow-xl 
                    overflow-hidden border border-[#E5EAF0] max-h-[300px] overflow-y-auto'
      >
       <div
        onClick={() => {
         setSelectedState(undefined)
         setIsStateOpen(false)
        }}
        className='flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#FFFFFF]'
       >
        <div
         className={`w-5 h-5 rounded-full border-2 
          ${
           !selectedState
            ? 'border-[#4E83E4]'
            : 'border-[#CED8E0]'
          }
          flex items-center justify-center`}
        >
         {!selectedState && (
          <div className='w-3 h-3 bg-[#4E83E4] rounded-full' />
         )}
        </div>
        <span className='text-base text-[#33393D]'>
         {t('ticket.ticketTopBar.all')}
        </span>
       </div>

       <div className='border-t border-[#E5EAF0]' />

       {stateOptions.map((item) => (
        <div key={item.id}>
         <div
          onClick={() => {
           setSelectedState(item.id)
           setIsStateOpen(false)
          }}
          className='flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#FFFFFF]'
         >
          <div
           className={`w-5 h-5 rounded-full border-2 
              ${
               selectedState === item.id
                ? 'border-[#4E83E4]'
                : 'border-[#CED8E0]'
              }
              flex items-center justify-center`}
          >
           {selectedState === item.id && (
            <div className='w-3 h-3 bg-[#4E83E4] rounded-full' />
           )}
          </div>

          <span className='text-base text-[#33393D]'>
           {item.name}
          </span>
         </div>

         <div className='border-t border-[#E5EAF0]' />
        </div>
       ))}
      </div>
     )}
    </div>

    <div className='flex items-center gap-2'>
     <span className='text-sm text-[#33393D]'>
      {t('ticket.ticketTopBar.quickFilter')}
     </span>

     {quickFilters.map((key) => (
      <div
       key={key}
       onClick={() => setActiveFilter(key)}
       className={`px-4 py-1.5 rounded-lg text-sm cursor-pointer border
                ${
                 activeFilter === key
                  ? 'bg-[#4E83E4] text-white border-[#4E83E4]'
                  : 'bg-white text-[#33393D] border-[#CED8E0]'
                }`}
      >
       {key}
      </div>
     ))}
    </div>
   </div>
   <div className='mt-6'>
    <KTTable
     data={tableData}
     columns={columns}
     headerClass='bg-[#80868C]'
     rowClass='even:bg-[#F5F8FA]'
     colClass='!text-sm'
     showPagination={false}
    />
   </div>

   <div className='mt-4'>
    <KTPagination
     currentPage={currentPage}
     itemsPerPage={itemsPerPage}
     totalRecord={totalRecord}
     onPageChange={setCurrentPage}
     setItemsPerPage={(size) => {
      setItemsPerPage(size)
      setCurrentPage(1)
     }}
    />
   </div>
  </>
 )
}

export default AllTicketScreen
